import { useRef, useCallback } from 'react';
import { Box, TextField } from '@mui/material';

import { useStore } from 'logic';
import { observer } from 'mobx-react-lite';
import { useInputCallback, useTypography } from 'logic/hooks';

export const SessionName = observer(() => {
  const { fontEditorial } = useTypography();
  const { uiPermissions, uiSessionNameStore } = useStore();

  // Add null checks to prevent errors when stores aren't initialized
  if (!uiPermissions || !uiSessionNameStore) {
    return null;
  }

  const { cannotEditCurrentSession } = uiPermissions;
  const { savedName, inputName, setInputName, saveName } = uiSessionNameStore;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const isChanging = useRef(false);
  const isCancelling = useRef(false);

  const onNameInputChange = useInputCallback(setInputName);

  const changeName = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (inputRef.current) {
        isChanging.current = true;
        try {
          inputRef.current.blur();
          await saveName();
        } finally {
          isChanging.current = false;
        }
      }
    },
    [saveName],
  );

  const onNameInputBlur = useCallback(() => {
    if (isCancelling.current) {
      isCancelling.current = false;
      setInputName();
      return;
    }

    if (!isChanging.current) {
      void saveName();
    }
  }, [saveName, setInputName]);

  const blurOnEscape = useCallback((e: React.KeyboardEvent) => {
    if (inputRef.current && e.keyCode === 27) {
      // Escape
      isCancelling.current = true;
      inputRef.current.blur();
    }
  }, []);

  const nameValue = (inputName === undefined ? savedName : inputName) ?? '';

  return (
    <Box my={1} sx={{ minWidth: 0, maxWidth: '40vw', flexShrink: 1 }}>
      <form onSubmit={changeName}>
        <TextField
          inputRef={inputRef}
          fullWidth
          value={nameValue}
          onChange={onNameInputChange}
          onKeyDown={blurOnEscape}
          onBlur={onNameInputBlur}
          variant="standard"
          InputProps={{
            readOnly: cannotEditCurrentSession,
          }}
          inputProps={{
            'aria-label': 'Project name',
          }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            '& .MuiInput-underline:before': {
              borderBottomColor: 'transparent',
              borderBottomWidth: 1,
              transitionDuration: '300ms',
            },
            '& .MuiInput-underline:hover:before': {
              borderBottomColor: '#bbb',
              borderBottomWidth: 1,
            },
            '& .MuiInput-root': {
              width: '100%',
              minWidth: 0,
            },
            '& .MuiInput-input': {
              fontFamily: fontEditorial,
              fontSize: 21,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textAlign: 'left',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiInput-input:focus': {
              textOverflow: 'clip',
            },
          }}
        />
      </form>
    </Box>
  );
});
